<template>
  <div class="blocklyArea">
    <transition name="slide-fade">
      <div class="overlays" key="overlay" v-show="overlayOpen">
        <div v-html="customOutputArea" v-if="customConsole" class="console-style"/>
        <textarea readonly v-model="outputArea" v-else class="console-style"/>
        <div class="overlay-control">
          <button class="btn btn--tab btn--blue" @click="stepCode" v-if="!disablePerStepExe"><i class="fas fa-step-forward"/>Per langkah</button>
          <button class="btn btn--tab" @click="stopStepCode" v-if="isStepping"><i class="fas fa-stop"/>Hentikan</button>
          <button class="btn btn--tab btn--blue" @click="runCode" v-else><i class="fas fa-play"/>Jalankan</button>
        </div>
      </div>
    </transition>
    <div class="toggle" key="toggle" :class="{'nudge-button': overlayOpen}">
      <button class="btn btn--tab" @click="overlayOpen = !overlayOpen">
        <i class="fas fa-terminal" v-if="!overlayOpen"/><i class="fas fa-times" v-else/>
        <div><span v-if="!overlayOpen">Buka</span><span v-else>Tutup</span><br/>konsol</div>
      </button>
      <button
        v-if="testCase.length > 0"
        class="btn btn--tab btn--blue"
        :class="{'btn--red': blockCount > maxBlocks}"
        @click="testCode"
      >
        <i class="fas fa-vial"/>
        <div>Uji kode</div>
        <small v-if="maxBlocks">Blok: {{blockCount}}/{{maxBlocks}}</small>
      </button>
    </div>
    <div id="blocklyDiv"></div>
  </div>
</template>

<script>
  import Blockly from 'blockly'
  import 'blockly/python'
  import 'blockly/javascript'
  import * as Id from 'blockly/msg/id'
  import DefaultBlocks from '@/utilities/Blockly/DefaultBlocks'

  export default {
    name: 'blockly',
    props: {
      toolbox: { type: String, default: DefaultBlocks },
      testCase: { type: Array, default: () => [] },
      maxBlocks: { type: Number, default: undefined },
      customBlocks: { type: Array, default: undefined },
      disablePerStepExe: { type: Boolean, default: false },
      customConsole: { type: Function, default: undefined }
    },
    data() {
      return {
        workspace: null,
        overlayOpen: false,
        jsInterpreter: null,
        highlightPause: false,
        outputArea: '',
        customOutputArea: '',
        code: '',
        currentInput: 0,
        resultIndex: 0,
        blockCount: 0,
        result: [],
        isStepping: false,
        isTesting: false,
      }
    },
    computed: {
      userDataStore() {
        return this.$store.state.userStore
      },
    },
    mounted() {
      Blockly.setLocale(Id)
      this.setupBlockly()
      this.customBlocksSetup()
      this.generateCodeAndLoadIntoInterpreter()
      this.workspace.addChangeListener((event) => {
        if (!(event instanceof Blockly.Events.Ui)) {
          // Something changed. Parser needs to be reloaded.
          this.generateCodeAndLoadIntoInterpreter()
          this.blockCount = this.workspace.getAllBlocks().length
        }
      })
    },
    methods: {
      setupBlockly() {
        const blocklyArea = document.getElementsByClassName('blocklyArea')[0]
        const blocklyDiv = document.getElementById('blocklyDiv')
        this.workspace = Blockly.inject(blocklyDiv,
            {
              toolbox: this.toolbox,
              zoom: {
                controls: true,
                wheel: true,
              },
              oneBasedIndex: false,
              grid: {
                spacing: 20,
                length: 3,
                colour: this.userDataStore.darkMode ? '#333' : '#ccc',
              },
              move: {
                scrollbars: true,
                drag: true,
                wheel: true
              },
              theme: this.userDataStore.darkMode ? Blockly.Themes.Dark : null,
              renderer: "zelos",
            })
        const onresize = () => {
          // Compute the absolute coordinates and dimensions of blocklyArea.
          let element = blocklyArea
          let x = 0
          let y = 0
          do {
            x += element.offsetLeft
            y += element.offsetTop
            element = element.offsetParent
          } while (element)
          // Position blocklyDiv over blocklyArea.
          blocklyDiv.style.left = x + 'px'
          blocklyDiv.style.top = y + 'px'
          blocklyDiv.style.width = blocklyArea.offsetWidth + 'px'
          blocklyDiv.style.height = blocklyArea.offsetHeight + 'px'
          Blockly.svgResize(this.workspace)
        }
        window.addEventListener('resize', onresize, false)
        onresize()
        Blockly.svgResize(this.workspace)
      },
      customBlocksSetup() {
        if (this.customBlocks) {
          for (let item of this.customBlocks) {
            Blockly.Blocks[item.type] = {
              init: function() {
                this.jsonInit(item);
              }
            }
            Blockly.JavaScript[item.type] = item.f
          }
        }
      },
      initApi(interpreter, scope) {
        // Add an API function for the alert() block, generated for "text_print" blocks.
        interpreter.setProperty(scope, 'alert',
          interpreter.createNativeFunction((text) => {
            text = arguments.length ? text : ''
            if (!this.isTesting) this.outputArea += '\n' + text
            this.result[this.resultIndex].push(text)
          }))

        // Add an API function for the prompt() block.
        let wrapper = (text) => {
          return interpreter.createPrimitive(prompt(text))
        }
        interpreter.setProperty(scope, 'prompt',
          interpreter.createNativeFunction(
            this.isTesting ?
              () => this.testCase[this.resultIndex].input[this.currentInput++]
              :
              wrapper
          ))

        // Add an API function for highlighting blocks.
        wrapper = (id) => {
          id = String(id || '')
          return interpreter.createPrimitive(this.highlightBlock(id))
        }
        interpreter.setProperty(scope, 'highlightBlock',
          interpreter.createNativeFunction(wrapper))
      },
      highlightBlock(id) {
        this.workspace.highlightBlock(id)
        this.highlightPause = true
      },
      resetStepUi(clearOutput) {
        this.workspace.highlightBlock(null)
        this.highlightPause = false
        this.isStepping = false

        if (clearOutput) {
          this.outputArea = '---------- Output ----------'
        }
      },
      stepCode() {
        if (!this.jsInterpreter) {
          // First statement of this code.
          // Clear the program output.
          this.resetStepUi(true)
          this.resultIndex = 0
          this.result[0] = []

          // eslint-disable-next-line no-undef
          this.jsInterpreter = new Interpreter(this.code, this.initApi)

          this.isStepping = true

          // And then show generated code in an alert.
          // In a timeout to allow the outputArea to reset first.
          setTimeout(() => {
            this.highlightPause = true
            this.stepCode()
          }, 1)
          return
        }
        this.highlightPause = false
        let hasMoreCode
        do {
          try {
            hasMoreCode = this.jsInterpreter.step()
          } finally {
            if (!hasMoreCode) {
              // Program complete, no more code to execute.
              this.outputArea += '\n\n<<======>> Program Selesai <<======>>'
              this.jsInterpreter = null
              this.resetStepUi(false)
              this.isStepping = false
            }
          }
          // Keep executing until a highlight statement is reached,
          // or the code completes or errors.
        } while (hasMoreCode && !this.highlightPause)
      },
      runCode() {
        if (!this.jsInterpreter) {
          // First statement of this code.
          // Clear the program output.
          this.resetStepUi(true)
          if (!this.isTesting) {
            this.resultIndex = 0
            this.result[0] = []
          }

          // And then show generated code in an alert.
          // In a timeout to allow the outputArea.value to reset first.

          // Begin execution
          this.highlightPause = false
          // eslint-disable-next-line no-undef
          this.jsInterpreter = new Interpreter(this.code, this.initApi)

          let runner = () => {
            if (this.jsInterpreter) {
              let hasMore
              try {
                hasMore = this.jsInterpreter.run()
              } catch(e) {
                this.outputArea += `\n\n${e}\n`
              }
              if (hasMore) {
                runner()
              } else {
                // Program is complete.
                if (!this.isTesting) this.outputArea += '\n\n<<======>> Program Selesai <<======>>'
                this.jsInterpreter = null
                if (runner) {
                  clearTimeout(runner)
                  runner = null
                }
                if (this.customConsole && !this.isTesting) {
                  this.customOutputArea = this.customConsole(this.result[0], this.testCase)
                }
                this.resetStepUi(false)
              }
            }
          }
          runner()
        }
      },
      testCode() {
        if (this.maxBlocks && this.blockCount > this.maxBlocks) {
          alert("Kamu melebihi batas jumlah blok yang diperbolehkan!")
          return
        }

        this.resultIndex = 0
        this.result = []
        this.isTesting = true

        for (let i in this.testCase) {
          this.resultIndex = i
          this.result[i] = []
          this.currentInput = 0
          this.runCode()
        }

        setTimeout(() => {
          this.isTesting = false
          this.$emit('on-finish-testing', this.result, this.blockCount)
        }, 50)
      },
      stopStepCode() {
        this.outputArea += '\n\n<<======>> Program dihentikan <<======>>'
        this.jsInterpreter = null
        this.resetStepUi(false)
        this.isStepping = false
      },
      generateCodeAndLoadIntoInterpreter() {
        // Generate JavaScript code and parse it.
        Blockly.JavaScript.STATEMENT_PREFIX = 'highlightBlock(%1)\n'
        let infiniteLoopTrapNumber = 1000
        Blockly.JavaScript.INFINITE_LOOP_TRAP =
          `if (--window.LoopTrap == 0)
          throw "[ERROR] Kamu menyentuh batas iterasi sebanyak ${infiniteLoopTrapNumber}!"\n`
        Blockly.JavaScript.addReservedWords('highlightBlock')
        this.code = `window.LoopTrap = ${infiniteLoopTrapNumber};` +  Blockly.JavaScript.workspaceToCode(this.workspace)
        this.resetStepUi(true)
      }
    }
  }
</script>

<style lang="scss" scoped>
  @import 'src/styles/colors';

  .blocklyArea {
    height: 100%;
    position: relative;
    overflow-x: hidden;
  }

  .toggle, .overlays {
    position: absolute;
    right: 0;
    z-index: 100;
  }

  .toggle {
    display: flex;
    flex-direction: column;
    top: 3rem;
    transition: transform 0.3s ease;

    .btn {
      height: 100px;
      margin-bottom: 0.5rem;
    }

    i {
      font-size: 1.5rem;
      margin-bottom: 0.5rem;
    }

    &.nudge-button {
      transform: translateX(-21.5rem);
    }
  }

  .overlays {
    z-index: 100;
    background-color: rgba(white, 0.5);
  }

  .overlay-control {
    display: flex;
    padding: 0.5rem;
    margin-bottom: 5px;
    width: 20rem;

    * {
      flex-grow: 1;
    }

    & > *:last-child {
      margin: 0;
    }

    i {
      margin-right: 0.5rem;
    }
  }

  .console-style {
    font-size: 1rem;
    font-family: monospace;
    background-color: rgba(white, 0.5);
    border: none;
    color: black;
    backdrop-filter: blur(15px) saturate(300%);
    border-radius: 0;
    box-shadow: 0 10px 10px 0 rgba(black,0.2);
    height: 50vh;
    max-height: calc(100vh - 3.5rem);
    overflow-y: auto;
  }

  .dark-mode {
    .overlays {
      background-color: rgba($dark-mode-back-primary, 0.5);
    }

    .console-style {
      background-color: rgba($dark-mode-back-primary, 0.5);
      color: white;
    }
  }
</style>
